import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Kanban, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { v: "NEW", l: "Nouveau" },
  { v: "QUALIFIED", l: "Qualifié" },
  { v: "CONTACTED", l: "Contacté" },
  { v: "FOLLOW_UP", l: "Relance" },
  { v: "MEETING", l: "Rendez-vous" },
  { v: "QUOTE_REQUESTED", l: "Soumission" },
  { v: "PROPOSAL_SENT", l: "Proposition" },
  { v: "NEGOTIATION", l: "Négociation" },
  { v: "WON", l: "Gagné" },
  { v: "LOST", l: "Perdu" },
];

const stageColor = (v) => ({
  NEW: "border-cyan-500/40 bg-cyan-500/5",
  QUALIFIED: "border-emerald-500/40 bg-emerald-500/5",
  CONTACTED: "border-blue-500/40 bg-blue-500/5",
  FOLLOW_UP: "border-amber-500/40 bg-amber-500/5",
  MEETING: "border-purple-500/40 bg-purple-500/5",
  QUOTE_REQUESTED: "border-amber-500/40 bg-amber-500/5",
  PROPOSAL_SENT: "border-indigo-500/40 bg-indigo-500/5",
  NEGOTIATION: "border-fuchsia-500/40 bg-fuchsia-500/5",
  WON: "border-emerald-500/60 bg-emerald-500/10",
  LOST: "border-red-500/40 bg-red-500/5",
}[v] || "");

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/leads");
      setLeads(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (leadId, status) => {
    try {
      await api.patch(`/crm/leads/${leadId}`, { status });
      setLeads((prev) => prev.map((l) => l.lead_id === leadId ? { ...l, status } : l));
      toast.success("Statut mis à jour");
    } catch { toast.error("Erreur"); }
  };

  const remove = async (leadId) => {
    if (!confirm("Retirer ce lead du CRM ?")) return;
    try {
      await api.delete(`/crm/leads/${leadId}`);
      setLeads((prev) => prev.filter((l) => l.lead_id !== leadId));
      toast.success("Retiré");
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <Kanban className="h-7 w-7 text-cyan-400" /> Pipeline commercial
        </h1>
        <p className="text-muted-foreground mt-1">Suivez vos leads du premier contact au contrat signé.</p>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-center py-12">Chargement…</div>
      ) : leads.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-border bg-card/40" data-testid="empty-crm">
          <Kanban className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-semibold mb-1">Votre pipeline est vide</h3>
          <p className="text-sm text-muted-foreground mb-4">Ajoutez des opportunités depuis la page Opportunités.</p>
          <Link to="/opportunites"><Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950">Voir les opportunités</Button></Link>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {STAGES.slice(0, 10).map((stage) => {
            const stageLeads = leads.filter((l) => l.status === stage.v);
            return (
              <div key={stage.v} className={`rounded-xl border ${stageColor(stage.v)} p-3 min-h-[200px]`} data-testid={`crm-column-${stage.v}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="text-xs font-semibold uppercase tracking-wider">{stage.l}</div>
                  <Badge variant="outline" className="text-[10px] border-border">{stageLeads.length}</Badge>
                </div>
                <div className="space-y-2">
                  {stageLeads.map((lead) => (
                    <Card key={lead.lead_id} className="p-3 bg-card border-border group" data-testid={`crm-lead-${lead.lead_id}`}>
                      <Link to={`/opportunites/${lead.opportunity_id}`}>
                        <div className="text-sm font-semibold mb-1 line-clamp-1 hover:text-cyan-400">{lead.company_name}</div>
                      </Link>
                      <div className="text-[11px] text-muted-foreground mb-2">{lead.city} · Score {lead.ai_score}</div>
                      {lead.estimated_value && (
                        <div className="text-[11px] text-cyan-300 mb-2">
                          {new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(lead.estimated_value)}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Select value={lead.status} onValueChange={(v) => updateStatus(lead.lead_id, v)}>
                          <SelectTrigger className="h-7 text-[11px]" data-testid={`stage-select-${lead.lead_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => remove(lead.lead_id)}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </Card>
                  ))}
                  {stageLeads.length === 0 && <div className="text-[11px] text-muted-foreground text-center py-4">Aucun lead</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
